import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { b2bFetch } from './auth';
import TransitionDashboard from '../components/TransitionDashboard';

/* The entrepreneur portal for a TRANSITIONING firm - one working towards
 * becoming a B2B entrepreneur firm. Until Transition Day the firm sees its
 * transition dashboard and nothing else: the server has already cut every
 * member's roles down (get_current_b2b), so the full workspace's endpoints
 * would refuse them anyway. This file is the whole of what they get.
 *
 * The firm's own admin also gets Members, to bring their team in. Whoever they
 * add has transition access only until the firm goes live, because the access
 * cut is on the firm, not the person. No Claude, no documents, no uploads. */

const c = {
  navy: '#0a2947', gold: '#d09b2c', teal: '#0069a6', amber: '#b9770e',
  red: '#9a1c1c', ink: '#1d2733', subtle: '#58585b', line: '#e9e6e1',
};

// FastAPI validation errors arrive as a list; everything else as a string.
const errText = (e) => (Array.isArray(e?.detail)
  ? e.detail.map(d => d.msg).join('; ')
  : (e?.detail || e?.message || 'Something went wrong.'));

const btn = (primary) => ({
  background: primary ? c.teal : 'white', color: primary ? 'white' : c.teal,
  border: `1px solid ${c.teal}`, borderRadius: 6, padding: '0.4rem 0.9rem',
  fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
});
const input = { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #cfd4da', borderRadius: 6, fontSize: '0.9rem' };

function Members({ selfUuid }) {
  const [members, setMembers] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', send_email: true });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);   // { text, password? }

  const load = useCallback(async () => {
    try { setMembers((await b2bFetch('/api/b2b/transition/members')).members); }
    catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (ev) => {
    ev.preventDefault();
    setBusy(true); setErr(null); setNotice(null);
    try {
      const r = await b2bFetch('/api/b2b/transition/members', { method: 'POST', body: JSON.stringify(form) });
      setNotice({
        text: r.email_sent
          ? `${r.member.email} was added and emailed their sign-in details.`
          : `${r.member.email} was added. ${r.email_error ? 'The email could not be sent. ' : ''}Pass on their temporary password:`,
        password: r.email_sent ? null : r.temp_password,
      });
      setForm({ email: '', first_name: '', last_name: '', send_email: true });
      load();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email}? They will no longer be able to sign in.`)) return;
    setErr(null); setNotice(null);
    try { await b2bFetch(`/api/b2b/transition/members/${m.uuid}`, { method: 'DELETE' }); load(); }
    catch (e) { setErr(errText(e)); }
  };

  const reset = async (m) => {
    if (!window.confirm(`Reset the password for ${m.email}? They will be emailed a new temporary password.`)) return;
    setErr(null); setNotice(null);
    try {
      const r = await b2bFetch(`/api/b2b/transition/members/${m.uuid}/reset_password?send_email=true`, { method: 'POST' });
      setNotice({
        text: r.email_sent ? `A new temporary password was emailed to ${m.email}.` : `The email could not be sent. Pass on this temporary password to ${m.email}:`,
        password: r.email_sent ? null : r.temp_password,
      });
    } catch (e) { setErr(errText(e)); }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
      <h2 style={{ color: c.navy, fontWeight: 800, fontSize: '1.3rem', marginBottom: '0.25rem' }}>Members</h2>
      <p style={{ color: c.subtle, fontSize: '0.9rem', marginBottom: '1.25rem' }}>
        Bring your team in to follow the transition. Everyone you add sees this dashboard until Transition Day,
        then moves to your full Prosperity workspace with the rest of the firm.
      </p>

      {err && <div role="alert" style={{ background: 'rgba(154,28,28,0.06)', border: '1px solid rgba(154,28,28,0.3)', color: c.red, borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem' }}>{err}</div>}
      {notice && (
        <div role="status" style={{ background: 'rgba(0,105,166,0.07)', border: '1px solid rgba(0,105,166,0.3)', color: c.ink, borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem' }}>
          {notice.text}
          {notice.password && <div style={{ fontFamily: 'monospace', fontSize: '1rem', marginTop: 4 }}>{notice.password}</div>}
        </div>
      )}

      <form onSubmit={add} style={{ background: 'white', borderRadius: 10, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '1rem', marginBottom: '1.25rem' }}>
        <div style={{ fontWeight: 700, color: c.navy, marginBottom: '0.6rem' }}>Add a person</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem' }}>
          <input style={input} type="email" required placeholder="Email" aria-label="Email"
            value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <input style={input} placeholder="First name" aria-label="First name"
            value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
          <input style={input} placeholder="Last name" aria-label="Last name"
            value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
          <label style={{ fontSize: '0.85rem', color: c.subtle, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.send_email} onChange={e => setForm({ ...form, send_email: e.target.checked })} />
            Email them their sign-in details
          </label>
          <button type="submit" disabled={busy} style={btn(true)}>{busy ? 'Adding...' : 'Add'}</button>
        </div>
      </form>

      <div style={{ background: 'white', borderRadius: 10, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: 560 }}>
          <thead>
            <tr style={{ background: c.navy, color: 'white', textAlign: 'left' }}>
              <th style={{ padding: '0.55rem 0.8rem' }}>Name</th>
              <th style={{ padding: '0.55rem 0.8rem' }}>Email</th>
              <th style={{ padding: '0.55rem 0.8rem' }}>Signed in</th>
              <th style={{ padding: '0.55rem 0.8rem' }} />
            </tr>
          </thead>
          <tbody>
            {members === null && <tr><td colSpan={4} style={{ padding: '1rem', color: c.subtle }}>Loading...</td></tr>}
            {(members || []).map(m => (
              <tr key={m.uuid} style={{ borderTop: `1px solid ${c.line}` }}>
                <td style={{ padding: '0.55rem 0.8rem' }}>
                  {[m.first_name, m.last_name].filter(Boolean).join(' ') || '-'}
                  {m.role === 'B2B_ADMIN' && <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: c.teal }}>Admin</span>}
                </td>
                <td style={{ padding: '0.55rem 0.8rem' }}>{m.email}</td>
                <td style={{ padding: '0.55rem 0.8rem', color: c.subtle }}>
                  {m.needs_password_change ? 'Not yet' : m.last_login ? new Date(m.last_login).toLocaleDateString() : 'Yes'}
                </td>
                <td style={{ padding: '0.55rem 0.8rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {m.uuid !== selfUuid && (
                    <>
                      <button type="button" onClick={() => reset(m)} style={{ ...btn(false), padding: '0.2rem 0.6rem', fontSize: '0.78rem', marginRight: 6 }}>Reset password</button>
                      <button type="button" onClick={() => remove(m)} style={{ ...btn(false), padding: '0.2rem 0.6rem', fontSize: '0.78rem', color: c.red, borderColor: c.red }}>Remove</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
Members.propTypes = { selfUuid: PropTypes.string };

export default function TransitionWorkspace({ me, impersonator, onLogout, onReturnToAdmin }) {
  const isAdmin = (me.roles || []).includes('B2B_TRANSITION_ADMIN');
  const [tab, setTab] = useState('dashboard');
  const [data, setData] = useState(null);
  const [news, setNews] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await b2bFetch('/api/b2b/transition');
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(errText(e));
      }
      // Optional extra - the dashboard is complete without it.
      try {
        const a = await b2bFetch('/api/b2b/announcements');
        if (!cancelled) setNews(a || []);
      } catch { /* leave it empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const tabBtn = (id, label) => (
    <button type="button" onClick={() => setTab(id)} aria-current={tab === id ? 'page' : undefined}
      style={{
        background: 'transparent', border: 'none', color: 'white', fontWeight: 700, fontSize: '0.88rem',
        padding: '0.35rem 0.2rem', cursor: 'pointer',
        borderBottom: `3px solid ${tab === id ? c.gold : 'transparent'}`,
      }}>
      {label}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#E9E6E1', color: c.ink, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      {impersonator && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', background: c.amber, color: 'white', padding: '0.55rem 1rem', fontSize: '0.85rem', fontWeight: 600 }}>
          <i className="bi bi-incognito" style={{ fontSize: '1.05rem' }} />
          <span style={{ flex: 1, minWidth: 200 }}>
            Prosperity admin view - you&apos;re logged in as{impersonator.target_email ? <> <strong>{impersonator.target_email}</strong></> : ' this user'}.
          </span>
          <button type="button" onClick={onReturnToAdmin}
            style={{ background: 'white', color: c.amber, border: 'none', borderRadius: 6, padding: '0.3rem 0.8rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <i className="bi bi-arrow-left me-1" />Return to admin
          </button>
        </div>
      )}

      <div style={{ background: c.navy, color: 'white', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', padding: '0.6rem 1rem', borderBottom: `2px solid ${c.gold}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div aria-hidden="true" style={{ width: 26, height: 26, flex: '0 0 26px', background: c.gold, WebkitMask: 'url(/pca.png) center / contain no-repeat', mask: 'url(/pca.png) center / contain no-repeat' }} />
          <div style={{ lineHeight: 1.05 }}>
            <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>Prosperity</div>
            <div style={{ color: c.gold, fontWeight: 700, fontSize: '0.55rem', letterSpacing: '2px', textTransform: 'uppercase' }}>Entrepreneur</div>
          </div>
        </div>
        {isAdmin && (
          <nav style={{ display: 'flex', gap: '1rem' }}>
            {tabBtn('dashboard', 'Your transition')}
            {tabBtn('members', 'Members')}
          </nav>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', wordBreak: 'break-all' }}>{me.username}</span>
        <button type="button" onClick={onLogout}
          style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: 'white', borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer' }}>
          <i className="bi bi-box-arrow-right me-1" />Sign out
        </button>
      </div>

      {tab === 'members' && isAdmin ? (
        <Members selfUuid={me.uuid} />
      ) : err ? (
        <div style={{ maxWidth: 700, margin: '2rem auto', background: 'white', borderRadius: 10, padding: '1.5rem', color: c.subtle, textAlign: 'center' }}>
          {err}
        </div>
      ) : !data ? (
        <div style={{ padding: '3rem', textAlign: 'center' }}><div className="spinner-border" style={{ color: c.teal }} /></div>
      ) : (
        <TransitionDashboard data={data} announcements={news} />
      )}
    </div>
  );
}
TransitionWorkspace.propTypes = {
  me: PropTypes.object.isRequired,
  impersonator: PropTypes.object,
  onLogout: PropTypes.func.isRequired,
  onReturnToAdmin: PropTypes.func.isRequired,
};
